import type { Field, FilterOption } from '../search-panel-types';
import type { Category, Area } from '@ilteducation/content-ref-data-types/models/data-types';
import getCategoryTranslation from './get-category-translation';
import useLocale from './useLocale';
import { storeToRefs } from 'pinia';
import { useContentRefData } from '@ilteducation/content-ref-data';
import { AggregateCount } from '../types';

const useFormatFilterOptions = () => {
  const { t } = useLocale();
  const contentRefDataStore = useContentRefData();
  const { byId } = storeToRefs(contentRefDataStore);
  const categoryList = (counts: AggregateCount[], kind: string | undefined) => {
    const values: FilterOption[] = [];

    counts.forEach((item: AggregateCount) => {
      const category = byId.value('categories', item.key) as Category | undefined;
      // Only include categories associated with the current subscription - since we only store categories for current subscription
      if (category) {
        if (kind && category.kind !== kind) {
          return;
        }
        values.push({
          value: item.key,
          count: item.count,
          label: getCategoryTranslation(category),
        });
      }
    });
    return values;
  };

  const areaList = (counts: AggregateCount[]): FilterOption[] => {
    const values: FilterOption[] = [];

    counts.forEach((item: AggregateCount) => {
      const area = byId.value('areas', item.key) as Area;
      if (area) {
        values.push({
          value: item.key,
          count: item.count,
          label: area.name,
        });
      }
    });
    return values;
  }

  const languageLists = (counts: AggregateCount[]): FilterOption[] => {
    if (!counts.length) {
      return [];
    }

    return counts
      .reduce(
        (values: FilterOption[], item: AggregateCount) =>
          values.concat({
            value: item.key,
            count: item.count,
            label: t(`${item.key}_long`) as string,
          }),
        [] as FilterOption[],
      )
      .sort((a: FilterOption, b: FilterOption) => (a.label > b.label ? 1 : -1));
  };

  const audienceList = (counts: AggregateCount[]): FilterOption[] => {
    if (!counts.length) {
      return [];
    }

    return counts
      .reduce(
        (values: FilterOption[], item: AggregateCount) =>
          values.concat({
            value: item.key,
            count: item.count,
            label: t(`label_audience_${item.key}`) as string,
          }),
        [] as FilterOption[],
      )
      .sort((a: FilterOption, b: FilterOption) => (a.label > b.label ? 1 : -1));
  };

  const keywordsList = (counts: AggregateCount[]): FilterOption[] => {
    if (!counts.length) {
      return [];
    }

    return counts
      .reduce(
        (values: FilterOption[], item: AggregateCount) =>
          values.concat({
            value: item.key,
            count: item.count,
            label: t(`material_keywords.${item.key}`) as string,
          }),
        [] as FilterOption[],
      )
      .sort((a: FilterOption, b: FilterOption) => (a.label > b.label ? 1 : -1));
  };

  const transformToFilterOptions = (field: Field, counts: AggregateCount[]): FilterOption[] => {
    const key = field.aggregationKey;
    switch (true) {
      case key === 'properties.audiences':
        return audienceList(counts);
      case key === 'languages.text' || key === 'languages.audio' || key === 'languages.video' || key === 'language':
        return languageLists(counts);
      case key === 'categories':
        return categoryList(counts, field.kind);
      case key === 'keywords':
        return keywordsList(counts);
      case key === 'areas':
        return areaList(counts);
      default:
        return counts.map((item) => ({ label: item.key, count: item.count, value: item.key }));
    }
  };

  return {
    transformToFilterOptions,
  };
};

export default useFormatFilterOptions;
